import CreatableSelect from "react-select/creatable"
import Select from "react-select"
import { Link, useNavigate } from "react-router-dom"
import { SelectionComponentProps } from "../interfaces/button_types"
import { openFolder } from "./misc"
import { FaRegFolder } from "react-icons/fa"

/**
 * A bunch of old code that may or may still exist in the codebase
 * 
 * I'm keeping it here for now, but it may be removed in the future
 */

const css = 'rounded-2xl p-4 my-2 transition shadow-sm hover:shadow-md generic-button'

// Readonly parameter is used to determine if the component is readonly or not (which just visually grey's it out)
export const styles = (readonly: boolean) => (
    {
        control: (baseStyles: any, state: any) => ({
            ...baseStyles,
            borderRadius: "10px",
            borderColor: state.isFocused ? "blue" : "gray",
            boxShadow: state.isFocused ? "0 0 0 2px blue" : "none",
            minWidth: "200px",
            backgroundColor: readonly ? "#5777c9" : "white",
            color: "#000000",
            "&:hover": {
                borderColor: state.isFocused ? "blue" : "gray",
            },
            ".dark &": {
                backgroundColor: readonly ? "#292524" : "#1c1917",
                borderColor: state.isFocused ? "#5777c9" : "#44403c",
                color: "#e7e5e4",
                "&:hover": {
                    borderColor: state.isFocused ? "#0000ff" : "#57534e",
                },
            },
        }),
        menu: (baseStyles: any) => ({
            ...baseStyles,
            borderRadius: "10px",
            overflow: "hidden",
            display: readonly ? "none" : "block",
            backgroundColor: "white",
            ".dark &": {
                backgroundColor: "#1c1917",
            },
        }),
        menuList: (baseStyles: any) => ({
            ...baseStyles,
            borderRadius: "10px",
            backgroundColor: "white",
            ".dark &": {
                backgroundColor: "#1c1917",
            },
        }),
        option: (state: any) => ({
            padding: "5px 10px",
            backgroundColor: state.isFocused ? "#fafafa" : "white",
            color: "#000000",
            "&:hover": {
                backgroundColor: "#e0e0e6",
            },
            ".dark &": {
                backgroundColor: state.isFocused ? "#292524" : "#1c1917",
                color: "#e7e5e4",
                "&:hover": {
                    backgroundColor: "#292524",
                },
            },
        }),
        singleValue: (baseStyles: any) => ({
            ...baseStyles,
            color: "#000000",
            ".dark &": {
                color: "#e7e5e4",
            },
        }),
        input: (baseStyles: any) => ({
            ...baseStyles,
            color: "#000000",
            ".dark &": {
                color: "#e7e5e4",
            },
        }),
        placeholder: (baseStyles: any) => ({
            ...baseStyles,
            color: "#666666",
            ".dark &": {
                color: "#a8a29e",
            },
        }),
    }
)

/** General Orange button */
export function RouteButton({route, text, onClick, icon}: {route: string, text: string, onClick?: () => void, icon?: React.ReactNode}) {
    return(
      <Link className={css} to={route} onClick={onClick}>
        <div className="flex flex-row gap-2 items-center">
          {icon ?? ""}
          {text}
        </div>
      </Link>
    )
}
  
/** Goes back a page in the browser */
export function BackButton() {
    const navigate = useNavigate()

    return(
        <button type="button" className={css} onClick={() => navigate(-1)}>Back</button>
    )
}

type OrangeButtonProps = {
    children: React.ReactNode,
    type?: "button" | "submit",
    onSubmit?: () => void,
    onClick?: () => void,
    tooltip?: string,
    className?: string,
    testidName?: string,
    disabled?: boolean,
}
/**
 * A button component that serves as main buttons for whatever you want
 * (OrangeButton comes from the TrinityMEP website, but now it 's blue. Too lazy to change all instances of it)
 * 
 * @param children - Can be honestly whatever you want, just know that it's all wrapped in that button.
 * @param onClick - The function to call when the button is clicked
 * @param tooltip - The tooltip to show when the button is hovered (a question mark will appear)
 */
export function OrangeButton({children, type = "button", onSubmit, onClick, tooltip, className, testidName, disabled=false}: OrangeButtonProps){
    return (
        <button type={type} className={`${css} ${className}`} onClick={onClick} onSubmit={onSubmit} title={tooltip} data-tooltip-id="tooltip" data-testid={testidName ?? ""} disabled={disabled}>
            <div className="relative">
                {tooltip && <p className="absolute flex items-center justify-center w-5 h-5 text-sm text-gray-400 rounded-full -top-3 -right-3">?</p>}
                {children}
            </div>
        </button>
    )
}

export function OrangeButtonWithIcon({children, onClick, icon}: {children: React.ReactNode, onClick: () => void, icon: React.ReactNode}) {
    return (
        <OrangeButton onClick={onClick}>
            <div className="flex flex-row gap-2 items-center">
                {icon}
                {children}
            </div>
        </OrangeButton>
    )
}
type BottomFormButtonProps = {
    button_text: string,
    children?: React.ReactNode,
    hide_back_button?: boolean,
    className?: string,
}
/**
 * For use in Form Components
 *  
 * Optional: @param children prop to add more buttons for more features(You might want to use OrangeButton for this)
 * Optional: @param hide_back_button prop to hide the back button (used for the onboarding page)
 * Optional: @param className prop to add more classes to the button
 */
export function BottomFormButton({ button_text, children, hide_back_button = false, className}: BottomFormButtonProps) {
    return (
    <div className={`mx-auto text-center justify-center pt-5 gap-5 flex ${className}`}>
        {!hide_back_button && <BackButton/>}
        
        <OrangeButton type="submit" testidName={"submit-button"}>{button_text}</OrangeButton>

        {children}

    </div>
    )
}

export function CreateableSelectionComponent({Value = '', multiple, options, name, onChange, label, className, optional=false}: SelectionComponentProps){
    if (!options) {
        options = [{value: '', label: ''}]
    }
        
    const defaultOption = options.find((option) => option.value === Value)
    
    // Sets default to the defaultOption if it exists
    // if not, uses DefaultValue
    const selectDefaultValue = defaultOption
        ? defaultOption
        : {value: Value, label: Value}

    if (Value !== '') {
        options.splice(options.indexOf({value: Value, label: Value}), 1)
    }

    return (
        <div className={`flex flex-col justify-between ${className}`}>
            <label data-testid={name} id={`${name}-label`} htmlFor={name} className="py-2">{optional ? "" : <span className="text-red-500">*</span>} {label}</label>
            <CreatableSelect 
                inputId={name} 
                aria-labelledby={`${name}-label`} 
                value={selectDefaultValue} 
                options={options} 
                name={name} 
                placeholder="Search" 
                isMulti={multiple} 
                onChange={onChange} 
                classNamePrefix="react-select"
                styles = {styles(false)}
            />
        </div>
    )
}

/**
 * # The selection component 
 * 
 * ! WARNING: This lags when there are a lot of options. Need to find a solution soon.
 * 
 * @param defaultValue - The default value of the selection component
 * @param multiple - Whether the selection component is multiple (i don't think i've used this)
 * @param options - The options for the selection component (NEEDED - Must be an array of objects with value and label)
 * @param name - The name of the selection component
 * @param onChange - The function to call when the selection component changes (NEEDED - You would most likely use some useState hook to update the state)
 * @param label - The label of the selection component
 * @param readonly - Whether the selection component is readonly
 * @param className - The class name of the selection component
 */
export function SelectionComponent({Value: defaultValue = '', multiple, options, name, onChange, label, readonly, className, optional=false}: SelectionComponentProps){
    if (!options) {
        options = [{value: '', label: ''}]
        console.error("No options found for", name)
        return <div>No options found for {name}</div>
    }

    const defaultOption = options.find((option) => option.value === defaultValue)
    
    // This one is specifically for the manager (getting the email for defaultmanager)
    const defaultOptionValue = options.find((option) => option.label === defaultValue)?.value

    // If the default option value is not found, it will be undefined
    // This would cause an error if the manager's email is not found
    if (!defaultOptionValue) {
        // Kinda annoying to see in the console
        //console.warn("No default option value found for", name, "with options", options)
    }
    
    // Sets default to the defaultOption if it exists
    // if not, uses DefaultValue
    const selectDefaultValue = defaultOption || {value: defaultOptionValue || defaultValue, label: defaultValue}

    return (
        <div data-testid={name} className="flex flex-col justify-between">
            {label && <label id={`${name}-label`} htmlFor={name} className="py-2">{optional ? "" : <span className="text-red-500">*</span>} {label}:</label>}
            
            <Select inputId={name} 
            aria-labelledby={`${name}-label`} 
            value={selectDefaultValue} 
            onChange={onChange} 
            options={options} 
            name={name} 
            placeholder="Search" 
            isMulti={multiple} 
            classNamePrefix="react-select"
            className={className}
            styles = {styles(readonly ?? false)}/>
        </div>
    )
}

/**
 * ## Component for opening a folder in the file explorer
 * 
 * Default color is blue
 * 
 * It's a div instead of a button because when put in a form, it would submit the form for some reason
 * 
 * @param folder_path - The path to the folder to be opened
 * @returns A div that opens the folder in the file explorer
 */
export function OpenFolderButton({ folder_path, folder_name="Open Folder", drive="T" }: { folder_path: string, folder_name?: string, drive?: string }) {
    return (
        <>
            <div className="p-4 my-2 transition bg-blue-200 border border-blue-200 shadow-md cursor-pointer hover:bg-blue-400 rounded-xl dark:bg-indigo-700 dark:hover:bg-indigo-600 text-stone-900 dark:text-stone-50" onClick={() => openFolder(folder_path, drive)}>
                <div className="flex flex-row gap-2 items-center">
                    <FaRegFolder />
                    {folder_name}
                </div>
            </div>
        </>
    )
}

const card_css = 'bg-white border p-3 my-2 transition rounded-lg shadow-md text-left dark:bg-stone-800 dark:hover:bg-stone-800/20 dark:text-stone-100 dark:border-stone-400'

type CardDropdownButtonProps = {
    route?: string,
    text: string,
    onClick?: () => void
}


/** Clear Button used for the card dropdowns on the Dashboard */
export function CardDropdownButton({route, text, onClick}: CardDropdownButtonProps) {
    return(
        <>
            {route && <Link className={card_css + "border dashboard-inner-button-card"} to={route} onClick={onClick}>{text}</Link>}
            {!route && <button className={card_css + "border dashboard-inner-button-card"} onClick={onClick}>{text}</button>}
        </>
    )
}

export function CardDropdownOpenFolderButton({ folder_path, label="Open Folder" }: { folder_path: string, label?: string }) {
    return (
        <>
            <button className={card_css + "bg-white border-blue-300 hover:bg-blue-50 text-blue-900"} onClick={() => openFolder(folder_path)}>{label}</button>
        </>
    )
}